import * as React from 'react';
import styled from 'styled-components';
import { withMedia } from 'react-media-query-hoc';

import { SelectField, Text, Accordion } from '@origin-digital/style-guide';
import { Box, Horizontal } from '@origin-digital/layout';
import { markUpUtils } from '@origin-digital/utils-toolbox';
import { getMessage } from '../../../services/messages';
import ToolTip from '../../tool-tip/tool-tip';
import { FilterSection } from './common';
import { extraCostText, getGreenData } from '@origin-digital/plans';

export const getExtraGreenCost = (
  config: {
    value: string;
    extraCost: {
      residential: string;
      business: string;
    };
  }[],
  greenSelection: number,
  isBusiness: boolean,
): string => {
  const matched = config.find((c) => c.value === greenSelection.toString());
  if (matched) {
    const { business, residential } = matched.extraCost;
    return isBusiness ? business : residential;
  }
  return '';
};

const Container = styled(FilterSection)`
  padding: 0 0 0 24px;
`;

const MobileContainer = styled.div`
  width: 100%;

  .open {
    margin-bottom: 0;
  }

  .tool-tip__content {
    left: -280px;
    top: -50px;
  }
`;

const StyledSelectField = styled.div`
  label {
    width: 100%;
    height: 20px;
  }
  select {
    padding-right: 36px;
  }
  div {
    margin-bottom: 0;
  }
`;

interface GreenOptionsProps {
  media: any;
  renewableEnergy: {
    gas: string;
    electricity: string;
  };
  handleRenewableEnergyChange: (fuelType: string, value: Number) => void;
  disableElectricity: boolean;
  disableGas: boolean;
  isBusiness: boolean;
}

export class BaseGreenOptions extends React.Component<GreenOptionsProps> {
  state = { accordionOpen: false };

  accordionToggle = () => {
    this.setState({ accordionOpen: !this.state.accordionOpen });
  };

  handleGreenElectricityChange = (event) => {
    this.props.handleRenewableEnergyChange('electricity', event.target.value);
  };

  handleGreenGasChange = (event) => {
    this.props.handleRenewableEnergyChange('gas', event.target.value);
  };

  render() {
    const { media, renewableEnergy, disableElectricity, disableGas, isBusiness } = this.props;
    const greenData = getGreenData(isBusiness);

    const selectionControls = (
      <Horizontal>
        <Box width="100px" marginRight={4}>
          <StyledSelectField>
            <SelectField
              name="greenelec"
              label="Electricity"
              prompt={false}
              data-id="electricity-green"
              value={renewableEnergy.electricity}
              options={greenData['01']}
              onChange={this.handleGreenElectricityChange}
              disabled={disableElectricity}
              helpText={extraCostText(renewableEnergy.electricity, '01', isBusiness, true)}
            />
          </StyledSelectField>
        </Box>
        {!isBusiness && (
          <Box width="100px">
            <StyledSelectField>
              <SelectField
                name="greengas"
                label="Natural gas"
                prompt={false}
                data-id="gas-green"
                value={renewableEnergy.gas}
                options={greenData['02']}
                onChange={this.handleGreenGasChange}
                disabled={disableGas}
                helpText={extraCostText(renewableEnergy.gas, '02', isBusiness, true)}
              />
            </StyledSelectField>
          </Box>
        )}
      </Horizontal>
    );

    const tooltip = (
      <ToolTip id="filters-want-to-go-green" align="left">
        <span
          dangerouslySetInnerHTML={markUpUtils.createDangerousMarkupObject(
            getMessage('TOOLTIP_GREEN_ENERGY'),
          )}
        />
      </ToolTip>
    );

    return media.mobile ? (
      <MobileContainer>
        <Accordion
          label="Add green energy"
          condensed
          open={this.state.accordionOpen}
          onToggle={this.accordionToggle}
        >
          <Box paddingY={4} display="flex" direction="row">
            {selectionControls} {tooltip}
          </Box>
        </Accordion>
      </MobileContainer>
    ) : (
      <Container>
        <Box marginBottom={4}>
          <Text inline size="sm" fontWeight="medium">
            Add green energy
          </Text>
          {tooltip}
        </Box>
        {selectionControls}
      </Container>
    );
  }
}

export const GreenOptions = withMedia(BaseGreenOptions);
